import { useState, useCallback } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  vendorId: string;
  vendorName?: string;
  vendorCode?: string;
  status: string;
  orderDate: string;
  expectedDelivery: string | null;
  subtotal: number;
  tax: number;
  total: number;
  notes: string | null;
  submittedAt: string | null;
  submittedVia: string | null;
  createdBy: string | null;
  createdAt: string;
  lineItemCount?: number;
  lineItems?: POLineItem[];
  vendor?: { name: string; code: string | null; email: string | null };
}

export interface POLineItem {
  id: string;
  shopifyVariantId: string;
  shopifyProductId: string;
  shopifyInventoryItemId: string | null;
  vendorProductId: string | null;
  sku: string | null;
  productTitle: string | null;
  variantTitle: string | null;
  imageUrl: string | null;
  packSize: number;
  caseCost: number;
  offInvoice: number;
  byCheque: number;
  freeGoods: number;
  vendorDiscountPct: number;
  netCaseCost: number;
  unitCost: number;
  retailPrice: number;
  quantityOrdered: number;
  quantityReceived: number;
  lineTotal: number;
  sortOrder: number;
}

export interface POBuilderItem {
  shopifyProductId: string;
  shopifyVariantId: string;
  shopifyInventoryItemId: string;
  productTitle: string;
  variantTitle: string;
  productType: string;
  productVendor: string;
  tags: string[];
  sku: string;
  imageUrl: string | null;
  selectedOptions: Array<{ name: string; value: string }>;
  bagSize: string | null;
  retailPrice: number;
  qoh: number;
  vendorProductId: string | null;
  vendorSku: string | null;
  category: string | null;
  packSize: number;
  caseCost: number;
  offInvoice: number;
  byCheque: number;
  freeGoods: number;
  vendorDiscountPct: number;
  isDiscontinued: boolean;
  manualOrderQty: number | null;
  flaggedForReview: boolean;
  reviewNote: string | null;
  productMinLevel: number | null;
  netCaseCost: number;
  unitCost: number;
  margin: number;
  weeksSupply: number;
  avgWeeklyUnits: number;
  minWeeksSupply: number;
  suggestedQty: number | null;
  sortOrder: number;
}

export function usePurchaseOrders() {
  const authFetch = useAuthenticatedFetch();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPurchaseOrders = useCallback(
    async (params?: { status?: string; vendorId?: string }) => {
      setLoading(true);
      setError(null);
      try {
        const query = new URLSearchParams();
        if (params?.status) query.set("status", params.status);
        if (params?.vendorId) query.set("vendorId", params.vendorId);
        const url = `/api/purchase-orders${query.toString() ? `?${query}` : ""}`;
        const res = await authFetch(url);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        setPurchaseOrders(json.data || []);
        setTotal(json.total || 0);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [authFetch]
  );

  const loadPurchaseOrder = useCallback(
    async (id: string): Promise<PurchaseOrder | null> => {
      try {
        const res = await authFetch(`/api/purchase-orders/${id}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data;
      } catch (err: any) {
        setError(err.message);
        return null;
      }
    },
    [authFetch]
  );

  const loadBuilderData = useCallback(
    async (vendorId: string): Promise<POBuilderItem[]> => {
      try {
        const res = await authFetch(`/api/purchase-orders/builder/${vendorId}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data || [];
      } catch (err: any) {
        setError(err.message);
        return [];
      }
    },
    [authFetch]
  );

  const createPurchaseOrder = useCallback(
    async (data: {
      vendorId: string;
      status: "draft" | "submitted";
      notes?: string;
      expectedDelivery?: string;
      lineItems: Array<Record<string, unknown>>;
    }): Promise<PurchaseOrder | null> => {
      try {
        const res = await authFetch("/api/purchase-orders", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data;
      } catch (err: any) {
        setError(err.message);
        return null;
      }
    },
    [authFetch]
  );

  const updatePurchaseOrder = useCallback(
    async (
      id: string,
      data: {
        notes?: string;
        expectedDelivery?: string;
        lineItems: Array<Record<string, unknown>>;
      }
    ): Promise<PurchaseOrder | null> => {
      try {
        const res = await authFetch(`/api/purchase-orders/${id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data;
      } catch (err: any) {
        setError(err.message);
        return null;
      }
    },
    [authFetch]
  );

  const submitPurchaseOrder = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await authFetch(`/api/purchase-orders/${id}/submit`, {
          method: "POST",
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  const cancelPurchaseOrder = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await authFetch(`/api/purchase-orders/${id}/cancel`, {
          method: "POST",
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  const deletePurchaseOrder = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const res = await authFetch(`/api/purchase-orders/${id}`, {
          method: "DELETE",
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  const receivePurchaseOrder = useCallback(
    async (
      poId: string,
      data: {
        invoiceNumber?: string;
        receivedBy?: string;
        notes?: string;
        lineItems: Array<{
          poLineItemId: string;
          quantityReceived: number;
          quantityDamaged?: number;
          invoicedCaseCost?: number | null;
          notes?: string;
        }>;
      }
    ): Promise<{ data: PurchaseOrder | null; shopifySynced: boolean; shopifyErrors?: Array<{ field: string; message: string }> }> => {
      try {
        const res = await authFetch(`/api/purchase-orders/${poId}/receive`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return {
          data: json.data,
          shopifySynced: json.shopifySynced,
          shopifyErrors: json.shopifyErrors,
        };
      } catch (err: any) {
        setError(err.message);
        return { data: null, shopifySynced: false };
      }
    },
    [authFetch]
  );

  const loadReceivingHistory = useCallback(
    async (poId: string): Promise<any[]> => {
      try {
        const res = await authFetch(
          `/api/purchase-orders/${poId}/receive/history`
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        return json.data || [];
      } catch (err: any) {
        setError(err.message);
        return [];
      }
    },
    [authFetch]
  );

  return {
    purchaseOrders,
    total,
    loading,
    error,
    loadPurchaseOrders,
    loadPurchaseOrder,
    loadBuilderData,
    createPurchaseOrder,
    updatePurchaseOrder,
    submitPurchaseOrder,
    cancelPurchaseOrder,
    deletePurchaseOrder,
    receivePurchaseOrder,
    loadReceivingHistory,
  };
}
